/**
 * HeaderCreateMenu Container Component
 *
 * Manages state and logic for the header create menu.
 * @module components/HeaderCreateMenu
 */

import { Plus } from "lucide-react-native";
import React, { useCallback, useState } from "react";

import HeaderCreateMenuView from "./HeaderCreateMenuView";

import AddToWatchlistActionsheet from "@/components/organisms/AddToWatchlistActionsheet";
// design-system: button size snapped xl→lg (closed size scale)
import { Button } from "@/components/atoms/Button";
import useAddMenuActions from "@/hooks/useAddMenuActions";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Container component for header create menu
 * Manages popover open/close state and action handlers
 */
function HeaderCreateMenuContainer() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const {
    expandedAction,
    recentPlayerItems,
    handleWriteReport,
    handleAddNote,
    handleAddToWatchlist,
    resetExpandedAction,
    handleSelectPlayerForReport,
    handleSelectPlayerForNote,
    handleSelectPlayerForWatchlist,
    showWatchlistActionsheet,
    selectedPlayerForWatchlist,
    handleCloseWatchlistActionsheet,
    handleAddWatchlistPress,
    watchlistLoading,
  } = useAddMenuActions({
    onDismiss: () => {
      setIsOpen(false);
      resetExpandedAction();
    },
  });

  const handlePopoverClose = useCallback(() => {
    handleClose();
    resetExpandedAction();
  }, [handleClose, resetExpandedAction]);

  const renderTrigger = useCallback(
    (triggerProps: Record<string, unknown>): React.ReactElement => (
      <Button
        {...triggerProps}
        size="lg"
        variant="link"
        className="rounded-none px-3 py-2"
        accessible
        accessibilityLabel="Create new item"
        accessibilityRole="button"
        icon={Plus}
      />
    ),
    []
  );

  return (
    <>
      <HeaderCreateMenuView
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handlePopoverClose}
        isDark={isDark}
        expandedAction={expandedAction}
        recentPlayers={recentPlayerItems}
        onWriteReport={handleWriteReport}
        onAddNote={handleAddNote}
        onAddToWatchlist={handleAddToWatchlist}
        onSelectPlayerForReport={handleSelectPlayerForReport}
        onSelectPlayerForNote={handleSelectPlayerForNote}
        onSelectPlayerForWatchlist={handleSelectPlayerForWatchlist}
        renderTrigger={renderTrigger}
      />
      {selectedPlayerForWatchlist && (
        <AddToWatchlistActionsheet
          item={selectedPlayerForWatchlist}
          showActionsheet={showWatchlistActionsheet}
          onClose={handleCloseWatchlistActionsheet}
          onAddPress={handleCloseWatchlistActionsheet}
          onAddWatchlistPress={handleAddWatchlistPress}
          onAddPlayerToWatchlistSuccess={handleCloseWatchlistActionsheet}
          loading={watchlistLoading}
          isPlayerInAnyWatchlist={Boolean(
            selectedPlayerForWatchlist.isWatchedByCurrentUser
          )}
          isDark={isDark}
        />
      )}
    </>
  );
}

export default HeaderCreateMenuContainer;
